import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FieldWrapper, Icon, Label, PageHeader, Tag, Text } from 'components';
import { theme, toRem } from 'design';
import { CardType } from 'types';
import { getPaymentTypeIcon } from 'utils';

export const SectionTitle = styled(Label)`
  font-size: ${toRem(14)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    font-size: ${toRem(13)};
  }
`;

export const ListTitle = styled(SectionTitle)`
  padding: 0 0 ${toRem(16)} ${toRem(10)};
`;

// Layout
const SettingsBreakpoint = 1000;

export const SettingsLayout = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.layout.gutter.small)};
  width: 100%;
  padding: 0 ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)};
  }

  @media (min-width: ${SettingsBreakpoint}px) {
    grid-template-columns:
      auto
      minmax(${toRem(125)}, ${toRem(200)})
      minmax(0, ${toRem(675)})
      auto;
    grid-gap: ${toRem(theme.layout.gutter.large)};
    padding: 0 0;
  }
`;

// Settings Nav
export const SettingsNav = styled.nav`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  width: 100%;
  margin-top: ${toRem(33)};

  @media (min-width: ${SettingsBreakpoint}px) {
    grid-column: 2/3;
    margin-top: ${toRem(68)};
  }
`;

export const SettingsNavItem = styled(NavLink)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: ${toRem(44)};
  margin-bottom: ${toRem(5.5)};
  padding: 0 ${toRem(11)};
  border-radius: ${toRem(11)};
  background-color: transparent;
  transition: background-color ${theme.transition.button.default};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(14)};
  line-height: 100%;
  text-decoration: none;
  color: ${theme.color.neutral[500]};
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background-color: ${theme.color.neutral[50]};
    color: ${theme.color.neutral[700]};
  }

  &.active {
    background-color: ${theme.color.neutral[50]};
    color: ${theme.color.secondary[400]};
    font-weight: ${theme.font.weight.semiBold};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

// Content
export const SettingsContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(22)};
  width: 100%;
  margin-bottom: ${toRem(22)};

  @media (min-width: ${SettingsBreakpoint}px) {
    grid-column: 3/4;
    grid-gap: ${toRem(44)};
  }
`;

export const SettingsPageHeader = styled(PageHeader).attrs({
  appearance: 'transparent',
})`
  grid-column: 1/1;
  width: 100%;
  margin-bottom: 0;
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
  border-bottom: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;
    padding: 0 0 0 0;
  }

  @media (min-width: ${SettingsBreakpoint}px) {
    margin-bottom: 0;
    padding: ${toRem(theme.layout.gutter.small)} 0 0 ${toRem(theme.layout.gutter.large)};
  }
`;

// Sections
export const SettingsGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(22)};
  width: 100%;
`;

interface SettingsSectionProps {
  fluidButton?: boolean;
}

export const SettingsSection = styled.section<SettingsSectionProps>`
  justify-self: center;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;

  button {
    width: ${toRem(105)};
    padding: 0;
  }

  ${({ fluidButton }) =>
    fluidButton &&
    css`
      button {
        width: auto;
        max-width: fit-content;
        padding: 0 ${toRem(22)};
      }
    `}
`;

export const SettingsSectionHeader = styled.header`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(22)};
`;

export const SettingsContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(22)};
  width: 100%;

  @media (min-width: ${SettingsBreakpoint}px) {
    grid-gap: ${toRem(44)};
    padding-left: ${toRem(theme.layout.gutter.large)};
    border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

export const SettingsTag = styled(Tag)`
  width: 100%;
  border-radius: ${toRem(11)} ${toRem(11)} 0 0;
  background-color: ${theme.color.neutral[200]};
  padding: ${toRem(18)};
  justify-content: center;
  text-align: center;
  font-size: ${toRem(12)};
  line-height: 125%;
  text-transform: none;
  top: ${toRem(6)};

  @media (max-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(20)};
  }
`;

interface SettingsSectionContentProps {
  columns?: number | 'auto';
  light?: boolean;
}

const SettingsSectionContainerStyles = css`
  width: 100%;
  padding: ${toRem(22)};
  border-radius: ${toRem(11)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(33)};
  }
`;

export const SettingsSectionContent = styled.section<SettingsSectionContentProps>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: ${toRem(11)} ${toRem(16.5)};
  background-color: ${({ light }) => (light ? theme.color.neutral[50] : theme.color.neutral[100])};
  ${SettingsSectionContainerStyles}

  ${({ columns }) =>
    columns &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        grid-template-columns: repeat(${columns}, 1fr);
        grid-gap: ${toRem(11)} ${toRem(22)};
      }
    `}

  ${({ columns }) =>
    columns === 'auto' &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        grid-template-columns: minmax(0, 1fr) auto;
        grid-gap: ${toRem(33)};
        align-items: center;
      }
    `}

    .day-segment-add-button {
    margin-top: ${toRem(16)};
  }
`;

// Forms
export const SettingsFieldWrapper = styled(FieldWrapper)`
  margin-bottom: 0;
`;

export const SubmitGroup = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  p {
    margin-bottom: ${toRem(8.25)};
  }
`;

export const SettingsEmailText = styled(Text)`
  width: 100%;
  overflow: hidden;
  word-wrap: break-word;
`;

export const MissingMethodMessage = styled(Text).attrs({
  size: 'small',
})`
  margin-top: ${toRem(11)};
`;

const PMAccountNumberLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
`;

const PMIcon = styled(Icon)`
  margin-right: ${toRem(5.5)};
`;

interface PaymentMethodAccountNumberProps {
  cardType?: CardType | undefined;
  cardNumber?: string;
  isProcessing?: boolean;
  showIcon?: boolean;
}

export const PaymentMethodAccountNumber = ({ cardType, cardNumber, isProcessing, showIcon = true }: PaymentMethodAccountNumberProps) => (
  <PMAccountNumberLayout>
    {showIcon && cardType && !isProcessing && <PMIcon label={`Payment Method Type: ${cardType}`} icon={getPaymentTypeIcon(cardType)} width={32} height={20} />}

    <Text size="default" fontWeight="medium">
      {isProcessing ? <>•••• •••• •••• ••••</> : cardType === CardType.AMERICAN_EXPRESS.toLowerCase() ? <>•••• •••••• •{cardNumber}</> : <>•••• •••• •••• {cardNumber}</>}
    </Text>
  </PMAccountNumberLayout>
);
